/**
 * - Allows users to configure the request body.
 * - Supports JSON and plain text formats
 * - Provides JSON validation and beautification
 * - Disables body for GET requests
 */

import React from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { Box, MenuItem, Select, FormControl, Button, Typography } from '@mui/material';
import { BodyState, BodyType } from './types';

interface BodyTabProps {
  body: BodyState;
  setBody: React.Dispatch<React.SetStateAction<BodyState>>;
  method: string;
  viewMode: boolean;
}

const BodyTab = ({ body, setBody, method, viewMode }: BodyTabProps) => {
  // Validate JSON format (only if type is JSON and there's content)
  const isJsonValid = () => {
    if (body.type !== 'json' || !body.raw.trim()) return true;

    try {
      JSON.parse(body.raw);
      return true;
    } catch {
      return false;
    }
  };

  // Beautify JSON content
  const handleBeautify = () => {
    if (viewMode) return;
    try {
      const obj = JSON.parse(body.raw);
      setBody((prev) => ({ ...prev, raw: JSON.stringify(obj, null, 2) }));
    } catch (e) {
      // Invalid JSON, do nothing
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '255px',
        gap: '1.5rem',
      }}
    >
      {/* Type Selector Row */}
      <Box sx={{ display: 'flex', alignItems: 'center', mt: '-10px' }}>
        <FormControl size="small">
          <Select
            value={body.type}
            variant="standard"
            disabled={viewMode}
            onChange={(e) => setBody((prev) => ({ ...prev, type: e.target.value as BodyType }))}
            sx={{ fontSize: '12px', fontWeight: 'bold', minWidth: '100px' }}
          >
            <MenuItem value="none">No Body</MenuItem>
            <MenuItem value="json" disabled={method === 'GET'}>
              JSON
            </MenuItem>
            <MenuItem value="text" disabled={method === 'GET'}>
              Plain Text
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* CodeMirror Editor Area */}
      {body.type !== 'none' ? (
        <>
          <Box
            sx={{
              border: '1px solid #d0d7de',
              borderRadius: '4px',
              overflow: 'hidden',
              fontSize: '12px',
              backgroundColor: viewMode ? '#f5f5f5' : '#fff',
              '& .cm-editor': { outline: 'none' },
              '& .cm-content': { textAlign: 'left' },
            }}
          >
            <CodeMirror
              value={body.raw}
              height="180px"
              extensions={body.type === 'json' ? [json()] : []}
              onChange={(value) => setBody((prev) => ({ ...prev, raw: value }))}
              editable={!viewMode}
              readOnly={viewMode}
              theme="light"
              basicSetup={{
                lineNumbers: true,
                foldGutter: true,
                highlightActiveLine: true,
                dropCursor: true,
                allowMultipleSelections: false,
                indentOnInput: true,
              }}
            />
          </Box>
          {/* JSON Error Message */}
          {body.type === 'json' && !isJsonValid() && (
            <Typography
              sx={{
                color: 'error.main',
                fontSize: '12px',
                mt: 0.5,
              }}
            >
              Invalid JSON format
            </Typography>
          )}
        </>
      ) : (
        <Box sx={{ p: 10, textAlign: 'center', bgcolor: '#f9f9f9', borderRadius: '4px' }}>
          <Typography variant="body2" color="text.secondary">
            This request does not have a body.
          </Typography>
        </Box>
      )}

      {/* Footer Actions */}
      {body.type === 'json' && !viewMode && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: '-12px', mb: '-8px' }}>
          <Button
            onClick={handleBeautify}
            size="small"
            sx={{
              textTransform: 'none',
              color: 'text.secondary',
              fontSize: '12px',
              textDecoration: 'underline',
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.main',
                textDecoration: 'underline',
              },
            }}
          >
            Beautify JSON
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BodyTab;
